/*
 * useQrAdisyonRealtime
 *
 * Realtime listener for the QR "Adisyon / Hesabım" screen. Subscribes to
 * Reverb/Pusher channels so the customer instantly sees new items, payments,
 * and discounts the waiter applies on the POS side. Falls back to polling
 * the public adisyon endpoint when the websocket connection drops, exactly
 * the same pattern as `useTableRealtimeWithFallback`.
 *
 * Channels listened on (best-effort, the hook silently ignores channels
 * that the backend has not yet wired up):
 *   - restaurant.{restaurantId}.tables   (.table.status-changed)
 *   - restaurant.{restaurantId}.orders   (.new-qr-order, .order.updated)
 *   - qr.{uniqueUrl}                     (.adisyon.updated, .order.updated)
 *
 * Whenever any relevant event arrives the supplied `onRefresh` callback is
 * called - the parent component is expected to re-fetch the adisyon snapshot
 * via `getQrAdisyon` (the snapshot itself is the canonical source of truth).
 */

import { useCallback, useEffect, useRef } from 'react';
import echo from '../services/echo';

export interface UseQrAdisyonRealtimeOptions {
  /** QR uniqueUrl (path UUID). Required so the public channel can be joined. */
  uniqueUrl: string | null | undefined;
  /** Restaurant id from the latest adisyon snapshot, used for tenant channels. */
  restaurantId: number | null | undefined;
  /** Table id from the latest adisyon snapshot, used to filter tenant events. */
  tableId?: number | null | undefined;
  /**
   * Refresh callback - the hook calls this whenever an event arrives or while
   * the websocket is disconnected (every `pollIntervalMs`).
   */
  onRefresh: () => void;
  /** Polling interval used as fallback when WS is unavailable. Default 10s. */
  pollIntervalMs?: number;
  /** Disable the hook entirely (e.g. while the QR page is invalid). */
  disabled?: boolean;
}

const RESTAURANT_ORDER_EVENTS = ['.new-qr-order', '.order.updated', '.qr.order.updated'];
const RESTAURANT_TABLE_EVENTS = ['.table.status-changed'];
const QR_PUBLIC_EVENTS = ['.adisyon.updated', '.order.updated', '.payment.created'];

export function useQrAdisyonRealtime(opts: UseQrAdisyonRealtimeOptions): void {
  const { uniqueUrl, restaurantId, tableId, onRefresh, pollIntervalMs = 10000, disabled = false } = opts;

  // Always read the freshest callback through a ref so we don't have to put
  // the parent's lambda into the effect-deps (which would tear down the
  // websocket on every render).
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const fire = useCallback(() => {
    onRefreshRef.current();
  }, []);

  useEffect(() => {
    if (disabled) return;
    if (!uniqueUrl) return;

    let isConnected = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (pollTimer) return;
      pollTimer = setInterval(() => {
        onRefreshRef.current();
      }, pollIntervalMs);
    };
    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    // ---------- Subscribe to QR-specific public channel ----------
    // Channel name uses the path UUID - best for the case where we don't yet
    // know the restaurant id (first paint, before the first /adisyon arrives).
    const qrChannelName = `qr.${uniqueUrl}`;
    const qrChannel = echo.channel(qrChannelName);
    QR_PUBLIC_EVENTS.forEach((evt) => {
      try {
        qrChannel.listen(evt, fire);
      } catch {
        /* channel may reject unknown events on some backends — ignore */
      }
    });

    // ---------- Subscribe to per-restaurant channels (when known) ----------
    let restaurantOrdersChannelName: string | null = null;
    let restaurantTablesChannelName: string | null = null;
    if (restaurantId) {
      restaurantOrdersChannelName = `restaurant.${restaurantId}.orders`;
      restaurantTablesChannelName = `restaurant.${restaurantId}.tables`;

      const ordersChannel = echo.channel(restaurantOrdersChannelName);
      RESTAURANT_ORDER_EVENTS.forEach((evt) => {
        try {
          ordersChannel.listen(evt, fire);
        } catch {
          /* ignore */
        }
      });

      const tablesChannel = echo.channel(restaurantTablesChannelName);
      RESTAURANT_TABLE_EVENTS.forEach((evt) => {
        try {
          tablesChannel.listen(evt, (payload: any) => {
            // Only react to events for *our* table when we know the table id.
            if (tableId && payload && Number(payload.table_id) !== Number(tableId)) {
              return;
            }
            fire();
          });
        } catch {
          /* ignore */
        }
      });
    }

    // ---------- Connection state -> polling fallback ----------
    const connector = (echo as any).connector?.pusher;
    const onConnected = () => {
      isConnected = true;
      stopPolling();
      // Reconciliation - pull canonical state on (re)connect.
      onRefreshRef.current();
    };
    const onDisconnected = () => {
      isConnected = false;
      startPolling();
    };

    if (connector?.connection) {
      connector.connection.bind('connected', onConnected);
      connector.connection.bind('disconnected', onDisconnected);
      connector.connection.bind('unavailable', onDisconnected);
      connector.connection.bind('failed', onDisconnected);
      if (connector.connection.state === 'connected') {
        isConnected = true;
      } else {
        startPolling();
      }
    } else {
      // No connector available (e.g. SSR or echo init failed) — pure polling.
      startPolling();
    }

    return () => {
      stopPolling();
      try {
        echo.leaveChannel(qrChannelName);
        if (restaurantOrdersChannelName) echo.leaveChannel(restaurantOrdersChannelName);
        if (restaurantTablesChannelName) echo.leaveChannel(restaurantTablesChannelName);
      } catch {
        /* ignore */
      }
      if (connector?.connection) {
        try {
          connector.connection.unbind('connected', onConnected);
          connector.connection.unbind('disconnected', onDisconnected);
          connector.connection.unbind('unavailable', onDisconnected);
          connector.connection.unbind('failed', onDisconnected);
        } catch {
          /* ignore */
        }
      }
      // Suppress unused-variable lint in some configs
      void isConnected;
    };
  }, [uniqueUrl, restaurantId, tableId, pollIntervalMs, disabled, fire]);
}
